import React, { useMemo, useState } from 'react'
import { MdDownload, MdOutlineQrCode2, MdOutlineStyle, MdOutlineWhatsapp } from 'react-icons/md'
import { toast } from 'react-toastify'
import Modal from '..'
import Button from '../../Form/Button'
import Input from '../../Form/Input'
import { QR_DEFAULTS, QR_COLORS, QR_SIZE, renderWhatsAppQRCode, whatsappLink } from '../../../utils/whatsAppQRCode'
import styles from './qrCode.module.css'

export default function ModalWhatsAppQRCode({ inbox, closeModal }) {
    const [model, setModel] = useState('card')
    const [title, setTitle] = useState(QR_DEFAULTS.title)
    const [subtitle, setSubtitle] = useState(QR_DEFAULTS.subtitle)
    const [color, setColor] = useState(QR_DEFAULTS.color)
    const [downloading, setDownloading] = useState(false)
    const phone = inbox?.metadata?.phone_number || inbox?.phone
    const link = whatsappLink(phone)
    const missingText = model === 'card' && (!title.trim() || !subtitle.trim())
    const preview = useMemo(() => {
        try {
            const canvas = renderWhatsAppQRCode({ phone, model, title, subtitle, color })
            return { canvas, url: canvas.toDataURL('image/png') }
        } catch (error) {
            return { error: error.message }
        }
    }, [phone, model, title, subtitle, color])

    const download = () => {
        if (!preview.canvas || missingText || downloading) return
        setDownloading(true)
        try {
            preview.canvas.toBlob(blob => {
                if (!blob) {
                    toast.error('Não foi possível gerar o PNG. Tente novamente.')
                    setDownloading(false)
                    return
                }
                const url = URL.createObjectURL(blob)
                const anchor = document.createElement('a')
                anchor.href = url
                anchor.download = `whatsapp-${link.split('/').pop()}-${model === 'card' ? 'card' : 'qr-code'}.png`
                document.body.appendChild(anchor)
                anchor.click()
                anchor.remove()
                setTimeout(() => URL.revokeObjectURL(url), 10000)
                setDownloading(false)
            }, 'image/png')
        } catch {
            setDownloading(false)
            toast.error('Não foi possível baixar o PNG. Tente novamente.')
        }
    }

    return (
        <Modal icon={<MdOutlineQrCode2 />} title="QR Code de divulgação" description="Uma nova conversa começa com a câmera do celular." closeModal={closeModal} fitContent>
            <div className={styles.editor} role="region" aria-label="Editor de QR Code">
                <div className={styles.number}>
                    <MdOutlineWhatsapp />
                    <div><strong>{inbox.name || 'WhatsApp'}</strong><span>{phone || 'Número indisponível'}</span></div>
                    <small>Destino do QR Code</small>
                </div>
                <div className={styles.workspace}>
                    <div className={styles.settings}>
                        <fieldset className={styles.models}>
                            <legend>1. Escolha o modelo</legend>
                            {[
                                { id: 'plain', title: 'Só QR Code', description: 'Simples, com fundo branco', icon: <MdOutlineQrCode2 /> },
                                { id: 'card', title: 'Card com textos', description: 'Pronto para divulgar', icon: <MdOutlineStyle /> },
                            ].map(option => <label key={option.id} className={`${styles.option} ${model === option.id ? styles.selected : ''}`}>
                                <input type="radio" name="qr-model" value={option.id} checked={model === option.id} onChange={() => setModel(option.id)} />
                                {option.icon}<span><strong>{option.title}</strong><small>{option.description}</small></span>
                            </label>)}
                        </fieldset>
                        {model === 'card' ? <div className={styles.texts}>
                            <h3>2. Dê o seu toque</h3>
                            <p>Os textos já estão prontos. Edite como preferir.</p>
                            <label htmlFor="qr-title">Título <small>{title.length}/48</small></label>
                            <Input id="qr-title" type="text" value={title} maxLength={48} onChange={event => setTitle(event.target.value)} />
                            <label htmlFor="qr-subtitle">Chamada <small>{subtitle.length}/72</small></label>
                            <Input id="qr-subtitle" type="text" value={subtitle} maxLength={72} onChange={event => setSubtitle(event.target.value)} />
                            {missingText && <span role="status" className={styles.error}>Preencha os dois textos para baixar o card.</span>}
                            <fieldset className={styles.colors}>
                                <legend>Cor dos detalhes</legend>
                                <div className={styles.colorOptions}>
                                    {QR_COLORS.map(option => <label key={option.value} className={styles.colorOption}>
                                        <input type="radio" name="qr-color" value={option.value} checked={color === option.value} onChange={() => setColor(option.value)} />
                                        <span className={styles.swatch} style={{ backgroundColor: option.value }} aria-hidden="true">{color === option.value ? '✓' : ''}</span>
                                        <span>{option.label}</span>
                                    </label>)}
                                </div>
                            </fieldset>
                        </div> : <p className={styles.tip}>Ideal para incluir em artes, embalagens e materiais da sua empresa.</p>}
                    </div>
                    <div className={styles.preview}>
                        <span className={styles.previewLabel}>PRÉVIA DO SEU QR CODE</span>
                        {preview.url ? <img src={preview.url} alt={`Prévia do ${model === 'card' ? 'card' : 'QR Code'} para ${phone}`} /> : <p role="alert" className={styles.error}>{preview.error}</p>}
                        <small>PNG · {QR_SIZE} × {QR_SIZE} px</small>
                        {link && <a href={link} target="_blank" rel="noopener noreferrer">Testar link do WhatsApp ↗</a>}
                    </div>
                </div>
                <div className={styles.footer}>
                    <p>Ao escanear, o cliente abre uma conversa com este número no WhatsApp.</p>
                    <div><Button variant="secondary" fullWidth={false} onClick={closeModal}>Fechar</Button><Button fullWidth={false} disabled={!preview.url || missingText || downloading} onClick={download}><MdDownload />{downloading ? 'Preparando…' : 'Baixar PNG'}</Button></div>
                </div>
            </div>
        </Modal>
    )
}
